import "./index.css";
import {
  initBv,
  bvApi,
  type BvSession,
  mountShell,
  statRow,
  dataTable,
  card,
  emptyState,
  pill,
  openModal,
  flash,
  fmtMoney,
  fmtDate,
  skeletonCard,
  h,
} from "./bv-init";

interface Config {
  enabled: boolean;
  auto_hold: boolean;
  hold_days: number;
}
interface Stats {
  held: number;
  held_amount: number;
  due: number;
  released: number;
  released_30d: number;
  refunded: number;
  disputed: number;
}
interface Hold {
  id: number;
  order_ref: string;
  customer_name: string | null;
  contact: string | null;
  amount: number;
  currency: string;
  state: "held" | "released" | "refunded" | "disputed";
  hold_until: string | null;
  due: boolean;
  created_at: string;
  resolved_at: string | null;
}
interface Event {
  kind: string;
  note: string | null;
  at: string;
  order_ref?: string;
  customer_name?: string | null;
  amount?: number;
  currency?: string;
}

let session: BvSession;
let currency = "JMD";

const STATE_TONE: Record<string, string> = { held: "primary", released: "ok", refunded: "bad", disputed: "warning" };
const fmtInt = (n: number | string) => Number(n || 0).toLocaleString();

boot();

async function boot() {
  try {
    session = await initBv();
  } catch (err) {
    renderFatal(err);
    return;
  }
  currency = session.merchant.currency_code || "JMD";
  mountShell({
    brandIcon: "wallet",
    brandLogo: "/logo.svg",
    title: "Escrow",
    subtitle: `${session.merchant.name || "Your store"} · hold & release`,
    poweredBy: "Marketplace",
    tabs: [
      { id: "overview", label: "Overview", icon: "wallet", render: renderOverview },
      { id: "holds", label: "Holds", icon: "inbox", render: renderHolds },
      { id: "settings", label: "Settings", icon: "settings", render: renderSettings },
    ],
  });
}

function renderFatal(err: unknown) {
  const root = document.getElementById("root")!;
  root.innerHTML = "";
  root.append(
    h(
      "div",
      { class: "bv-fatal" },
      h("strong", null, "Couldn't start"),
      h("p", { class: "bv-muted" }, (err as any)?.message || "No session token found."),
    ),
  );
}

async function renderOverview(host: HTMLElement) {
  host.innerHTML = "";
  host.append(skeletonCard());
  const data = await bvApi<{ config: Config; stats: Stats; recent: Event[] }>("/api/overview").catch(() => null);
  host.innerHTML = "";
  if (!data) {
    host.append(emptyState({ icon: "alert", title: "Couldn't load", text: "Please try again." }));
    return;
  }
  const s = data.stats;
  host.append(
    card({
      title: "How holds work",
      body: h(
        "p",
        { class: "bv-muted" },
        "This tracks a hold/release status on orders you've already been paid for — it doesn't custody or move money. “Mark released” and “Mark refunded” update your workflow here only; the funds already moved through your normal Inkress checkout, and any real refund must be issued from the Inkress order itself.",
      ),
    }),
  );
  host.append(
    statRow([
      { k: "On hold", v: fmtInt(s.held), icon: "clock" },
      { k: "Value on hold", v: fmtMoney(Number(s.held_amount), currency), icon: "wallet", tone: "accent" },
      { k: "Due to release", v: fmtInt(s.due), icon: "alert", tone: Number(s.due) > 0 ? "bad" : undefined },
      { k: "Released · 30d", v: fmtMoney(Number(s.released_30d), currency), icon: "check", tone: "ok" },
    ]),
  );

  host.append(
    card({
      title: "Track holds on paid orders",
      action: h("button", { class: "primary", onClick: () => doSync(host) }, "Import paid orders"),
      body: h(
        "p",
        { class: "bv-muted" },
        data.config.auto_hold
          ? `New paid orders are flagged on hold for ${data.config.hold_days} day${data.config.hold_days === 1 ? "" : "s"}, then can be marked released. Run this to bring in recent paid orders.`
          : "Auto-hold is off — turn it on in Settings to start tracking holds.",
      ),
    }),
  );

  host.append(
    card({
      title: "Hold window elapsed",
      action:
        Number(s.due) > 0
          ? h("button", { class: "primary", onClick: () => doReleaseDue(host) }, `Mark ${s.due} released`)
          : h("span", { class: "bv-muted" }, "Nothing due"),
      body: h(
        "p",
        { class: "bv-muted" },
        Number(s.due) > 0
          ? `${s.due} hold${Number(s.due) === 1 ? " has" : "s have"} passed the hold window and can be marked released.`
          : "Holds appear here once their hold window elapses.",
      ),
    }),
  );

  host.append(
    card({
      title: "Recent activity",
      body: data.recent.length
        ? dataTable<Event>({
            columns: [
              { head: "When", cell: (r) => fmtDate(r.at, true) },
              { head: "Order", cell: (r) => `#${r.order_ref}` },
              { head: "Customer", cell: (r) => r.customer_name || "—" },
              { head: "Event", cell: (r) => pill(r.kind, STATE_TONE[r.kind] || "") },
              { head: "Amount", num: true, cell: (r) => (r.amount != null ? fmtMoney(Number(r.amount), r.currency || currency) : "—") },
            ],
            rows: data.recent,
          })
        : emptyState({ icon: "inbox", title: "No activity yet", text: "Import paid orders to start tracking holds." }),
    }),
  );
}

async function doSync(host: HTMLElement) {
  const r = await bvApi<{ created: number; skipped?: string }>("/api/sync", { method: "POST" }).catch(() => null);
  if (!r) return flash("Import failed", "error");
  if (r.skipped) return flash("Auto-hold is off — enable it in Settings.", "warning");
  flash(r.created ? `Placed ${r.created} order${r.created === 1 ? "" : "s"} on hold.` : "No new paid orders to hold.", r.created ? "success" : "info");
  renderOverview(host);
}

async function doReleaseDue(host: HTMLElement) {
  const r = await bvApi<{ released: number }>("/api/holds/release-due", { method: "POST" }).catch(() => null);
  if (!r) return flash("Release failed", "error");
  flash(r.released ? `Marked ${r.released} hold${r.released === 1 ? "" : "s"} released.` : "Nothing was due.", r.released ? "success" : "info");
  renderOverview(host);
}

async function renderHolds(host: HTMLElement) {
  host.innerHTML = "";
  let state = "";
  const list = h("div");
  const states = ["", "held", "released", "refunded", "disputed"];
  const btns = states.map((sv) =>
    h(
      "button",
      {
        class: sv === "" ? "secondary" : "ghost",
        onClick: () => {
          state = sv;
          btns.forEach((b, i) => {
            (b as HTMLElement).className = states[i] === state ? "secondary" : "ghost";
          });
          load();
        },
      },
      sv === "" ? "All" : sv.charAt(0).toUpperCase() + sv.slice(1),
    ),
  );
  const seg = h("div", { class: "bv-row" }, ...btns);
  const load = async () => {
    list.innerHTML = "";
    list.append(skeletonCard());
    const d = await bvApi<{ holds: Hold[] }>(`/api/holds${state ? `?state=${state}` : ""}`).catch(() => ({ holds: [] as Hold[] }));
    list.innerHTML = "";
    list.append(
      d.holds.length
        ? dataTable<Hold>({
            columns: [
              { head: "Order", cell: (hd) => `#${hd.order_ref}` },
              { head: "Customer", cell: (hd) => hd.customer_name || "—" },
              { head: "Amount", num: true, cell: (hd) => fmtMoney(Number(hd.amount), hd.currency || currency) },
              { head: "Hold until", cell: (hd) => (hd.hold_until ? fmtDate(hd.hold_until) : "—") },
              {
                head: "State",
                cell: (hd) =>
                  hd.state === "held" && hd.due ? pill("due", "warning") : pill(hd.state, STATE_TONE[hd.state] || ""),
              },
            ],
            rows: d.holds,
            rowActions: (hd) => h("button", { class: "secondary", onClick: () => openHold(hd, host) }, "Manage"),
          })
        : emptyState({ icon: "inbox", title: "No holds", text: "Import paid orders from the Overview tab." }),
    );
  };
  host.append(card({ title: "Escrow ledger", action: seg, body: list }));
  load();
}

function openHold(hd: Hold, host: HTMLElement) {
  const body = h("div", { class: "bv-stack" });
  body.append(
    h(
      "div",
      { class: "bv-row", style: { justifyContent: "space-between" } },
      h("span", { class: "bv-muted" }, `Order #${hd.order_ref}`),
      pill(hd.state, STATE_TONE[hd.state] || ""),
    ),
    h("p", null, h("strong", null, fmtMoney(Number(hd.amount), hd.currency || currency)), ` · ${hd.customer_name || "Customer"}`),
    h(
      "p",
      { class: "bv-muted" },
      hd.contact ? `${hd.contact} · ` : "",
      hd.hold_until ? `Hold until ${fmtDate(hd.hold_until)}` : "No hold window",
    ),
  );
  const history = h("div", { class: "bv-stack" }, h("div", { class: "bv-skeleton", style: { width: "60%" } }));
  body.append(h("div", { class: "bv-label" }, "History"), history);
  bvApi<{ events: Event[] }>(`/api/holds/${hd.id}`)
    .then((d) => {
      history.innerHTML = "";
      if (!d.events.length) history.append(h("p", { class: "bv-muted" }, "No events."));
      for (const e of d.events) {
        history.append(
          h(
            "div",
            { class: "bv-row", style: { justifyContent: "space-between", gap: "10px" } },
            h("span", null, pill(e.kind, STATE_TONE[e.kind] || ""), " ", h("span", { class: "bv-muted" }, e.note || "")),
            h("span", { class: "bv-muted", style: { whiteSpace: "nowrap" } }, fmtDate(e.at, true)),
          ),
        );
      }
    })
    .catch(() => {
      history.innerHTML = "";
      history.append(h("p", { class: "bv-muted" }, "Couldn't load history."));
    });

  let close = () => {};
  const act = (verb: string, label: string, primary = false, danger = false) => ({
    label,
    primary,
    danger,
    onClick: () => {
      bvApi(`/api/holds/${hd.id}/${verb}`, { method: "POST" })
        .then(() => {
          flash(`Hold ${verb === "release" ? "released" : verb === "refund" ? "refunded" : "disputed"}.`, "success");
          close();
          renderHolds(host);
        })
        .catch((e) => flash(e?.message === "bad_state" ? "That action isn't allowed in this state." : "Action failed", "error"));
      return false;
    },
  });

  const actions: any[] = [{ label: "Close" }];
  if (hd.state === "held" || hd.state === "disputed") {
    actions.push(act("refund", "Mark refunded", false, true));
    actions.push(act("release", "Mark released", true));
  }
  if (hd.state === "held") actions.push(act("dispute", "Dispute"));

  const handle = openModal({ title: "Manage hold", body, actions });
  close = handle.close;
}

async function renderSettings(host: HTMLElement) {
  host.innerHTML = "";
  host.append(skeletonCard());
  const cfg = (await bvApi<{ config: Config }>("/api/config").catch(() => null))?.config;
  host.innerHTML = "";
  if (!cfg) {
    host.append(emptyState({ icon: "alert", title: "Couldn't load settings" }));
    return;
  }
  const enabled = h("input", { type: "checkbox" }) as HTMLInputElement;
  enabled.checked = cfg.enabled;
  const autoHold = h("input", { type: "checkbox" }) as HTMLInputElement;
  autoHold.checked = cfg.auto_hold;
  const holdDays = h("input", { type: "number", min: "0", max: "180", step: "1", value: String(cfg.hold_days) }) as HTMLInputElement;

  const save = h(
    "button",
    {
      class: "primary",
      onClick: async () => {
        const r = await bvApi("/api/config", {
          method: "POST",
          body: JSON.stringify({ enabled: enabled.checked, auto_hold: autoHold.checked, hold_days: +holdDays.value }),
        }).catch(() => null);
        flash(r ? "Settings saved" : "Save failed", r ? "success" : "error");
      },
    },
    "Save settings",
  );

  host.append(
    card({
      title: "Hold policy",
      action: save,
      body: h(
        "div",
        { class: "bv-stack" },
        field("Default hold window (days)", holdDays),
        h("label", { class: "bv-row" }, autoHold, h("span", null, "Automatically hold new paid orders")),
        h("label", { class: "bv-row" }, enabled, h("span", null, "Escrow enabled")),
        h(
          "p",
          { class: "bv-muted" },
          "Escrow tracks which paid orders are on hold and their release status. Release and refund are workflow states your team acts on — actual fund movement stays in your normal Inkress flow.",
        ),
      ),
    }),
  );
}

function field(label: string, input: HTMLElement): HTMLElement {
  return h("div", { class: "bv-field" }, h("label", { class: "bv-label" }, label), input);
}
